import { FormattedMessage } from 'react-intl';

import { Tooltip } from 'components/ui/tooltip';
import { cn } from 'utils/cn';
import { PromptBadge } from '../samplePrompts';

// Classic draws the chart palette with dark text, because white on either fails contrast. The new
// design keeps the chart colours for charts: Agentic is the neutral chip, New the amber tint. The AI
// surfaces carry the new design's scope, so they draw the `new:` pair in classic too.
const BADGES: Record<PromptBadge, { className: string; label: string; tooltip?: string }> = {
    agentic: {
        className: 'bg-chart-4 new:bg-neutral-tint new:text-foreground-secondary',
        label: 'Agentic',
        tooltip: 'Runs a multi-step analysis across several data sources',
    },
    new: {
        className: 'bg-chart-2 new:bg-secondary/30 new:text-changed-ink',
        label: 'New',
    },
};

/** Small chips marking a prompt's type, on the AI home's cards and the panel's rows alike. */
const PromptBadges: React.FC<{ badges?: PromptBadge[] }> = ({ badges }) => {
    if (!badges?.length) {
        return null;
    }

    return (
        <span className="flex shrink-0 flex-wrap items-center gap-1">
            {badges.map((badge) => {
                const { className, label, tooltip } = BADGES[badge];
                const chip = (
                    <span
                        key={badge}
                        className={cn('text-foreground text-caption rounded-lg px-1.5 font-medium', className)}
                    >
                        <FormattedMessage id={`ai-chat.sample-prompts.badges.${badge}`} defaultMessage={label} />
                        {/* The chip sits inside the prompt's button and cannot take focus to open its
                        tooltip, so the explanation also rides in the button's accessible name. */}
                        {tooltip ? (
                            <span className="sr-only">
                                {': '}
                                <FormattedMessage
                                    id={`ai-chat.sample-prompts.badges.${badge}-tooltip`}
                                    defaultMessage={tooltip}
                                />
                            </span>
                        ) : null}
                    </span>
                );
                return tooltip ? (
                    <Tooltip
                        key={badge}
                        content={
                            <FormattedMessage
                                id={`ai-chat.sample-prompts.badges.${badge}-tooltip`}
                                defaultMessage={tooltip}
                            />
                        }
                    >
                        {chip}
                    </Tooltip>
                ) : (
                    chip
                );
            })}
        </span>
    );
};

export default PromptBadges;
